import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Boxes,
  Check,
  Copy,
  Database,
  Eye,
  EyeOff,
  FileUp,
  FolderOpen,
  HardDrive,
  KeyRound,
  Loader2,
  Play,
  Plus,
  Table2,
  Terminal,
  Trash2,
} from 'lucide-react'
import { fetchApi, fetchProjectsApi, OrzForgeApiError } from '../lib/api'
import { useAuthStore } from '../store/auth'
import type {
  DbQueryResponse,
  Project,
  ProjectCreated,
  ProjectKeyCreated,
  ProjectList,
  StorageListResponse,
} from '../types'

export function ProjectsPage() {
  const queryClient = useQueryClient()
  const apiKey = useAuthStore((s) => s.apiKey)
  const setApiKey = useAuthStore((s) => s.setApiKey)

  const [name, setName] = useState('')
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [activeTab, setActiveTab] = useState<'database' | 'storage'>('database')
  const [copied, setCopied] = useState<string | null>(null)
  const [showSecret, setShowSecret] = useState(false)
  const [sql, setSql] = useState("SELECT name FROM sqlite_master WHERE type = 'table'")
  const [execSql, setExecSql] = useState('CREATE TABLE IF NOT EXISTS tasks (id INTEGER PRIMARY KEY, title TEXT)')
  const [file, setFile] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const keyMutation = useMutation({
    mutationFn: async () => {
      const data = (await fetchApi('/v1/keys', {
        method: 'POST',
        body: JSON.stringify({ name: 'OrzForge Projects' }),
      })) as ProjectKeyCreated
      return data.api_key
    },
    onSuccess: (key) => {
      setApiKey(key)
    },
  })

  const effectiveKey = apiKey ?? keyMutation.data

  const projectsQuery = useQuery<ProjectList>({
    queryKey: ['projects'],
    queryFn: async () => {
      if (!effectiveKey) {
        throw new Error('se necesita una API key para conectar con OrzForge Projects')
      }
      return (await fetchProjectsApi(effectiveKey, '/v1/projects')) as ProjectList
    },
    enabled: !!effectiveKey,
    staleTime: 30_000,
  })

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!effectiveKey) throw new Error('falta API key')
      return (await fetchProjectsApi(effectiveKey, '/v1/projects', {
        method: 'POST',
        body: JSON.stringify({ name }),
      })) as ProjectCreated
    },
    onSuccess: () => {
      setName('')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const tablesQuery = useQuery<DbQueryResponse>({
    queryKey: ['project-tables', selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject || !effectiveKey) throw new Error('selecciona un proyecto')
      return (await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/tables`,
      )) as DbQueryResponse
    },
    enabled: !!selectedProject && !!effectiveKey,
    staleTime: 15_000,
  })

  const storageQuery = useQuery<StorageListResponse>({
    queryKey: ['project-storage', selectedProject?.id],
    queryFn: async () => {
      if (!selectedProject || !effectiveKey) throw new Error('selecciona un proyecto')
      return (await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/storage/objects`,
      )) as StorageListResponse
    },
    enabled: !!selectedProject && !!effectiveKey,
    staleTime: 15_000,
  })

  const [queryResult, setQueryResult] = useState<DbQueryResponse | null>(null)
  const [queryRunning, setQueryRunning] = useState(false)
  const [queryError, setQueryError] = useState<string | null>(null)

  const [execResult, setExecResult] = useState<Record<string, unknown> | null>(null)
  const [execRunning, setExecRunning] = useState(false)
  const [execError, setExecError] = useState<string | null>(null)

  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: async (key: string) => {
      if (!selectedProject || !effectiveKey) throw new Error('selecciona un proyecto')
      return await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/storage/objects?key=${encodeURIComponent(key)}`,
        { method: 'DELETE' },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-storage', selectedProject?.id] })
    },
    onError: (err) => {
      setDeleteError(err instanceof OrzForgeApiError ? err.message : 'Error eliminando archivo')
    },
  })

  const tableColumns = useMemo(
    () => (queryResult?.results.length ? Object.keys(queryResult.results[0]) : []),
    [queryResult],
  )

  async function runQuery() {
    if (!selectedProject || !effectiveKey) return
    setQueryRunning(true)
    setQueryError(null)
    setQueryResult(null)
    try {
      const data = (await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/query`,
        {
          method: 'POST',
          body: JSON.stringify({ sql }),
        },
      )) as DbQueryResponse
      setQueryResult(data)
    } catch (err) {
      setQueryError(err instanceof OrzForgeApiError ? err.message : 'Error ejecutando query')
    } finally {
      setQueryRunning(false)
    }
  }

  async function runExec() {
    if (!selectedProject || !effectiveKey) return
    setExecRunning(true)
    setExecError(null)
    setExecResult(null)
    try {
      const data = (await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/exec`,
        {
          method: 'POST',
          body: JSON.stringify({ sql: execSql }),
        },
      )) as Record<string, unknown>
      setExecResult(data)
      tablesQuery.refetch()
    } catch (err) {
      setExecError(err instanceof OrzForgeApiError ? err.message : 'Error ejecutando DDL/DML')
    } finally {
      setExecRunning(false)
    }
  }

  async function uploadObject() {
    if (!selectedProject || !effectiveKey || !file) return
    setUploading(true)
    setUploadError(null)
    setUploadSuccess(null)
    try {
      const key = file.name
      await fetchProjectsApi(
        effectiveKey,
        `/v1/projects/${selectedProject.id}/storage/objects?key=${encodeURIComponent(key)}`,
        {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        },
      )
      setUploadSuccess(`Archivo "${key}" subido.`)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      queryClient.invalidateQueries({ queryKey: ['project-storage', selectedProject.id] })
    } catch (err) {
      setUploadError(err instanceof OrzForgeApiError ? err.message : 'Error subiendo archivo')
    } finally {
      setUploading(false)
    }
  }

  function copy(text: string, id: string) {
    navigator.clipboard.writeText(text)
    setCopied(id)
    setTimeout(() => setCopied(null), 1500)
  }

  if (!effectiveKey && keyMutation.isPending) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    )
  }

  const created: ProjectCreated | undefined = createMutation.data

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Proyectos</h1>
          <p className="text-sm text-gray-400">
            Crea un proyecto y obten una base de datos D1 + un bucket Storage propios.
          </p>
        </div>
        <div className="rounded-lg border border-white/10 bg-panel px-3 py-1.5 text-xs text-gray-400">
          {projectsQuery.data?.projects.length ?? 0} proyectos
        </div>
      </header>

      {(keyMutation.error || projectsQuery.error || createMutation.error) && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {keyMutation.error instanceof OrzForgeApiError
            ? keyMutation.error.message
            : projectsQuery.error instanceof OrzForgeApiError
              ? projectsQuery.error.message
              : createMutation.error instanceof OrzForgeApiError
                ? createMutation.error.message
                : 'Error de conexión'}
        </div>
      )}

      {!effectiveKey && !keyMutation.isPending && (
        <div className="rounded-xl border border-white/10 bg-panel p-5">
          <p className="mb-3 text-sm text-gray-300">
            Necesitamos una API key temporal para hablar con el worker de proyectos.
          </p>
          <button
            onClick={() => keyMutation.mutate()}
            disabled={keyMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
          >
            <KeyRound className="h-4 w-4" />
            Crear API key temporal
          </button>
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-panel p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/10 text-brand-400">
            <Plus className="h-5 w-5" />
          </div>
          <div>
            <p className="font-semibold">Nuevo proyecto</p>
            <p className="text-xs text-gray-500">Crea D1 + R2 Storage + endpoint propio.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="nombre-del-proyecto"
            className="flex-1 rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !name.trim() || !effectiveKey}
            className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <HardDrive className="h-4 w-4" />
            )}
            Crear
          </button>
        </div>
      </div>

      {created && (
        <div className="rounded-xl border border-green-500/20 bg-green-500/10 p-5">
          <p className="mb-3 text-sm font-semibold text-green-400">
            Proyecto <span className="text-white">{created.name}</span> creado
          </p>
          <div className="space-y-3 text-sm">
            <KeyRow label="Endpoint" value={created.endpoint} copied={copied} copy={copy} id="endpoint" />
            <KeyRow label="Bucket" value={created.r2_bucket_name} copied={copied} copy={copy} id="bucket" />
            <KeyRow label="Public Key" value={created.public_key} copied={copied} copy={copy} id="publicKey" />
            <KeyRow
              label="Secret Key"
              value={created.secret_key}
              copied={copied}
              copy={copy}
              id="secretKey"
              masked={!showSecret}
              onToggleMask={() => setShowSecret((s) => !s)}
            />
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-white/10 bg-panel p-5 lg:col-span-1">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-300">
            <Boxes className="h-4 w-4" />
            Tus proyectos
          </div>
          {projectsQuery.isLoading ? (
            <div className="flex h-24 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
            </div>
          ) : (
            <div className="space-y-1">
              {projectsQuery.data?.projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedProject(p)
                    setActiveTab('database')
                  }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                    selectedProject?.id === p.id
                      ? 'bg-brand-500/15 text-brand-300'
                      : 'text-gray-400 hover:bg-white/5 hover:text-gray-100'
                  }`}
                >
                  <span className="truncate">{p.name}</span>
                  <span className="ml-2 text-xs text-gray-600">{p.id.slice(0, 8)}…</span>
                </button>
              ))}
              {!projectsQuery.data?.projects.length && (
                <p className="py-4 text-center text-sm text-gray-500">No hay proyectos todavía.</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6 lg:col-span-2">
          {selectedProject ? (
            <>
              <div className="rounded-xl border border-white/10 bg-panel p-1">
                <div className="flex gap-1">
                  <button
                    onClick={() => setActiveTab('database')}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'database'
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-100'
                    }`}
                  >
                    <Database className="h-4 w-4" />
                    Database
                  </button>
                  <button
                    onClick={() => setActiveTab('storage')}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'storage'
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-100'
                    }`}
                  >
                    <FolderOpen className="h-4 w-4" />
                    Storage
                  </button>
                </div>
              </div>

              {activeTab === 'database' && (
                <>
                  <div className="rounded-xl border border-white/10 bg-panel p-5">
                    <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-300">
                      <Table2 className="h-4 w-4" />
                      Tablas en <span className="text-brand-300">{selectedProject.name}</span>
                    </div>
                    {tablesQuery.isLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {tablesQuery.data?.results.map((row, i) => (
                          <span
                            key={i}
                            className="rounded-md bg-white/5 px-2.5 py-1 text-sm font-mono text-brand-300"
                          >
                            {String((row as { name?: string }).name ?? JSON.stringify(row))}
                          </span>
                        ))}
                        {!tablesQuery.data?.results.length && (
                          <span className="text-sm text-gray-500">No hay tablas.</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-white/10 bg-panel p-5">
                    <label htmlFor="execSql" className="mb-2 block text-sm font-medium text-gray-300">
                      DDL / DML
                    </label>
                    <textarea
                      id="execSql"
                      value={execSql}
                      onChange={(e) => setExecSql(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-white/10 bg-surface p-3 font-mono text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        onClick={runExec}
                        disabled={execRunning}
                        className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
                      >
                        {execRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Terminal className="h-4 w-4" />
                        )}
                        {execRunning ? 'Ejecutando…' : 'Ejecutar DDL/DML'}
                      </button>
                    </div>
                    {execError && (
                      <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                        {execError}
                      </div>
                    )}
                    {execResult && (
                      <pre className="mt-3 overflow-x-auto rounded-lg border border-white/10 bg-surface p-3 text-xs text-gray-400">
                        {JSON.stringify(execResult, null, 2)}
                      </pre>
                    )}
                  </div>

                  <div className="rounded-xl border border-white/10 bg-panel p-5">
                    <label htmlFor="sql" className="mb-2 block text-sm font-medium text-gray-300">
                      Query SQL (read)
                    </label>
                    <textarea
                      id="sql"
                      value={sql}
                      onChange={(e) => setSql(e.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-white/10 bg-surface p-3 font-mono text-sm text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        onClick={runQuery}
                        disabled={queryRunning}
                        className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
                      >
                        {queryRunning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        {queryRunning ? 'Ejecutando…' : 'Ejecutar query'}
                      </button>
                    </div>
                    {queryError && (
                      <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                        {queryError}
                      </div>
                    )}
                  </div>

                  {queryResult && (
                    <div className="overflow-hidden rounded-xl border border-white/10 bg-panel">
                      <div className="max-h-[24rem] overflow-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="sticky top-0 bg-panel">
                            <tr className="border-b border-white/10">
                              {tableColumns.map((col) => (
                                <th key={col} className="px-4 py-3 font-semibold text-gray-300">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {queryResult.results.map((row, idx) => (
                              <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.02]">
                                {tableColumns.map((col) => (
                                  <td key={col} className="px-4 py-2.5 font-mono text-gray-400">
                                    {row[col] === null
                                      ? 'NULL'
                                      : typeof row[col] === 'object'
                                        ? JSON.stringify(row[col])
                                        : String(row[col])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {!queryResult.results.length && (
                          <div className="p-6 text-center text-sm text-gray-500">Sin resultados</div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'storage' && (
                <>
                  <div className="rounded-xl border border-white/10 bg-panel p-5">
                    <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-300">
                      <FileUp className="h-4 w-4" />
                      Subir archivo
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <input
                        ref={fileRef}
                        type="file"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        className="flex-1 rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-white file:mr-3 file:rounded file:border-0 file:bg-brand-600 file:px-3 file:py-1 file:text-white"
                      />
                      <button
                        onClick={uploadObject}
                        disabled={uploading || !file}
                        className="flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                        {uploading ? 'Subiendo…' : 'Subir'}
                      </button>
                    </div>
                    {uploadError && (
                      <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                        {uploadError}
                      </div>
                    )}
                    {uploadSuccess && (
                      <div className="mt-3 rounded-lg border border-green-500/20 bg-green-500/10 px-3 py-2 text-sm text-green-300">
                        {uploadSuccess}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-white/10 bg-panel p-5">
                    <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-300">
                      <FolderOpen className="h-4 w-4" />
                      Archivos en {selectedProject.r2_bucket_name}
                    </div>
                    {storageQuery.isLoading ? (
                      <div className="flex h-24 items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {deleteError && (
                          <div className="mb-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                            {deleteError}
                          </div>
                        )}
                        {storageQuery.data?.objects.map((obj) => (
                          <div
                            key={obj.key}
                            className="flex items-center justify-between rounded-lg border border-white/5 bg-surface px-3 py-2.5 text-sm"
                          >
                            <div className="min-w-0">
                              <p className="truncate font-mono text-gray-200">{obj.key}</p>
                              <p className="text-xs text-gray-500">{formatBytes(obj.size)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={`https://orzforge-projects.orzattydavid.workers.dev/v1/projects/${selectedProject.id}/storage/objects?key=${encodeURIComponent(obj.key)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded p-1.5 text-gray-400 hover:text-white"
                                title="Descargar"
                              >
                                <FolderOpen className="h-4 w-4" />
                              </a>
                              <button
                                onClick={() => {
                                  setDeleteError(null)
                                  deleteMutation.mutate(obj.key)
                                }}
                                disabled={deleteMutation.isPending}
                                className="rounded p-1.5 text-gray-400 hover:text-red-400"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {!storageQuery.data?.objects.length && (
                          <p className="py-4 text-center text-sm text-gray-500">No hay archivos.</p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-xl border border-white/10 bg-panel text-sm text-gray-500">
              Selecciona un proyecto para explorar su Database y Storage.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function KeyRow({
  label,
  value,
  copied,
  copy,
  id,
  masked,
  onToggleMask,
}: {
  label: string
  value: string
  copied: string | null
  copy: (text: string, id: string) => void
  id: string
  masked?: boolean
  onToggleMask?: () => void
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
      <span className="w-28 shrink-0 text-gray-400">{label}</span>
      <div className="flex flex-1 items-center gap-2">
        <input
          readOnly
          value={masked ? value.replace(/./g, '•') : value}
          type="text"
          className="flex-1 rounded border border-white/10 bg-surface px-2 py-1.5 text-xs text-white"
        />
        <button
          onClick={() => copy(value, id)}
          className="rounded p-1.5 text-gray-400 hover:text-white"
          title="Copiar"
        >
          {copied === id ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
        </button>
        {onToggleMask && (
          <button
            onClick={onToggleMask}
            className="rounded p-1.5 text-gray-400 hover:text-white"
            title={masked ? 'Mostrar' : 'Ocultar'}
          >
            {masked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        )}
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
